import { useState, useEffect, useMemo } from 'react';
import { calculateAmazonHours, calculateColisPriveHours } from '@/utils/salaryHours';
import { toast } from '@/components/ui/sonner';
import { Calculator, Calendar, ChevronLeft, ChevronRight, Clock, Edit2, Save, X, Users, Sun, Star, User, ChevronDown, ChevronUp, MapPin, Filter, Grid3X3, List, Download } from 'lucide-react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { InternalSalarySection } from '@/components/internal-staff/InternalSalarySection';
import { canManageInternal } from '@/components/internal-staff/shared';

// French public holidays (fixed dates - Easter Monday calculated dynamically)
const getPublicHolidays = (year) => {
  const getEasterMonday = (year) => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    
    const easterSunday = new Date(year, month - 1, day);
    const easterMonday = new Date(easterSunday);
    easterMonday.setDate(easterMonday.getDate() + 1);
    return easterMonday;
  };

  const easterMonday = getEasterMonday(year);
  
  return [
    { date: `${year}-01-01`, name: "Jour de l'An" },
    { date: `${year}-${String(easterMonday.getMonth() + 1).padStart(2, '0')}-${String(easterMonday.getDate()).padStart(2, '0')}`, name: "Lundi de Pâques" },
    { date: `${year}-05-01`, name: "Fête du Travail" },
    { date: `${year}-05-08`, name: "Victoire 1945" },
    { date: `${year}-07-14`, name: "Fête Nationale" },
    { date: `${year}-08-15`, name: "Assomption" },
    { date: `${year}-11-01`, name: "Toussaint" },
    { date: `${year}-11-11`, name: "Armistice" },
    { date: `${year}-12-25`, name: "Noël" },
  ];
};

// Calculate hours for Amazon based on tour type
const getValidationStatus = (schedule) => String(schedule?.validation_status || '').trim().toLowerCase();

const isMissionValidated = (schedule) => {
  const validationStatus = getValidationStatus(schedule);
  return validationStatus === 'approved' || validationStatus === 'validated';
};

const getValidationAwareMissionState = (schedule) => {
  const status = String(schedule?.status || '').trim().toLowerCase();
  const validationStatus = getValidationStatus(schedule);

  if (status !== 'present') return 'not_present';
  if (validationStatus === 'approved' || validationStatus === 'validated') return 'approved';
  if (validationStatus === 'rejected') return 'rejected';
  return 'pending';
};

const getValidationDisplayLabel = (schedule) => {
  const missionState = getValidationAwareMissionState(schedule);
  if (missionState === 'approved') return 'Validée';
  if (missionState === 'rejected') return 'Refusée';
  if (missionState === 'pending') return 'En attente de validation';
  return 'Aucune validation';
};

const isHoursCountableMission = (schedule) => getValidationAwareMissionState(schedule) === 'approved';
const hasPendingMissionValidation = (schedule) => getValidationAwareMissionState(schedule) === 'pending';
const hasRejectedMissionValidation = (schedule) => getValidationAwareMissionState(schedule) === 'rejected';
const isPresentWithoutValidation = (schedule) => {
  const status = String(schedule?.status || '').trim().toLowerCase();
  return status === 'present' && getValidationStatus(schedule) === '';
};

const formatHoursDecimalFr = (hours) => Number(hours || 0).toFixed(2).replace('.', ',');

const formatHoursHm = (hours) => {
  const totalMinutes = Math.round(Number(hours || 0) * 60);
  const hh = Math.floor(totalMinutes / 60);
  const mm = totalMinutes % 60;
  return `${hh}h${String(mm).padStart(2, '0')}`;
};

const formatHoursDual = (hours) => `${formatHoursHm(hours)} (${formatHoursDecimalFr(hours)} heures)`;

// Check if date is a Sunday
const isSunday = (dateStr) => {
  const date = new Date(dateStr + 'T12:00:00');
  return date.getDay() === 0;
};

// Check if date is a public holiday
const isPublicHoliday = (dateStr, holidays) => {
  return holidays.some(h => h.date === dateStr);
};

const getHolidayName = (dateStr, holidays) => {
  const holiday = holidays.find(h => h.date === dateStr);
  return holiday ? holiday.name : null;
};

const AdminCalculSalaire = ({ user, activeModule = "b2b" }) => {
  const [population, setPopulation] = useState('drivers');
  const [viewMode, setViewMode] = useState('month'); // 'day', 'week', 'month'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [schedules, setSchedules] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [editMode, setEditMode] = useState({});
  const [manualHours, setManualHours] = useState({});
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'
  const [expandedSites, setExpandedSites] = useState({}); // Track which sites are expanded
  
  // NEW: Display mode (individual = existing view, grid = new table view by site)
  const [displayMode, setDisplayMode] = useState('individual'); // 'individual', 'grid'
  const [selectedSiteFilter, setSelectedSiteFilter] = useState('all'); // Filter for grid view
  const [isMobileViewport, setIsMobileViewport] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 640 : false));

  useEffect(() => {
    const handleResize = () => setIsMobileViewport(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get public holidays
  const publicHolidays = useMemo(() => {
    const year = currentDate.getFullYear();
    return [...getPublicHolidays(year), ...getPublicHolidays(year + 1), ...getPublicHolidays(year - 1)];
  }, [currentDate]);

  const formatDateLocal = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const getDateRange = () => {
    const date = new Date(currentDate);
    
    if (viewMode === 'day') {
      const dateStr = formatDateLocal(date);
      return { start: dateStr, end: dateStr, dates: [dateStr] };
    } else if (viewMode === 'week') {
      const day = date.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      const monday = new Date(date);
      monday.setDate(date.getDate() + diff);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      
      const dates = [];
      const current = new Date(monday);
      while (current <= sunday) {
        dates.push(formatDateLocal(current));
        current.setDate(current.getDate() + 1);
      }
      
      return { start: formatDateLocal(monday), end: formatDateLocal(sunday), dates };
    } else {
      const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
      const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      
      const dates = [];
      const current = new Date(firstDay);
      while (current <= lastDay) {
        dates.push(formatDateLocal(current));
        current.setDate(current.getDate() + 1);
      }
      
      return { start: formatDateLocal(firstDay), end: formatDateLocal(lastDay), dates };
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentDate, viewMode]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      
      const [empRes, schedRes, sitesRes] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/schedules?start_date=${start}&end_date=${end}`, { withCredentials: true }),
        axios.get(`${API}/sites`, { withCredentials: true })
      ]);
      
      const allEmployees = (empRes.data || []).filter(e => e.role === 'employee');
      setEmployees(allEmployees);
      setSchedules(schedRes.data || []);
      setSites(sitesRes.data || []);
      
      // Heures manuelles : uniquement celles enregistrées en base (partagées entre admins) ; l'ancien stockage local n'est plus lu
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + direction);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  const isColisPriveSchedule = (schedule) => {
    const siteName = (schedule.site_name || '').toLowerCase();
    const tourType = (schedule.tour_type || '').toLowerCase();
    const comment = (schedule.comment || '').toLowerCase();
    
    return siteName.includes('colis') || siteName.includes('privé') || siteName.includes('prive') ||
           tourType.includes('colis') || tourType.includes('privé') ||
           comment.includes('livrés:') || comment.includes('retours:');
  };

  const getScheduleHours = (schedule) => {
    const key = `${schedule.user_id}_${schedule.date}`;

    // Règle P0: pas d'heures tant que la mission n'est pas validée admin
    if (!isHoursCountableMission(schedule)) {
      return 0;
    }
    
    // 1. First check local state (for unsaved edits)
    if (manualHours[key] !== undefined) {
      return manualHours[key];
    }
    
    // 2. Then check if schedule has manual_hours from database
    if (schedule.manual_hours !== undefined && schedule.manual_hours !== null) {
      return schedule.manual_hours;
    }
    
    // 3. Finally calculate based on mission type
    if (isColisPriveSchedule(schedule)) {
      // Colis Privé rule: ONLY approved/validated missions generate time
      // 1 validated mission = 7h45 (7.75h)
      if (!isMissionValidated(schedule)) {
        return 0;
      }
      return calculateColisPriveHours();
    } else {
      return calculateAmazonHours(schedule.tour_type);
    }
  };

  // Check if schedule has manual hours (from DB or local state)
  const hasManualHours = (schedule) => {
    const key = `${schedule.user_id}_${schedule.date}`;
    return manualHours[key] !== undefined || 
           (schedule.manual_hours !== undefined && schedule.manual_hours !== null);
  };

  const getEmployeeName = (emp) => {
    if (!emp) return 'Inconnu';
    if (emp.last_name && emp.first_name) {
      return `${emp.last_name} ${emp.first_name}`;
    }
    return emp.name || 'Inconnu';
  };

  // Get site name for an employee
  const getSiteName = (emp) => {
    if (!emp) return 'Sans site';
    if (emp.site_name) return emp.site_name;
    if (emp.site_id) {
      const site = sites.find(s => s.site_id === emp.site_id);
      return site ? site.name : 'Sans site';
    }
    return 'Sans site';
  };

  // Check if employee is active
  const isEmployeeActive = (emp) => {
    if (emp.isDeactivated) return false;
    if (emp.active === false) return false;
    return true;
  };

  // Get ALL employees/users who have schedules in the period
  // This includes deactivated employees - they MUST appear if they have prestations
  const employeesWithData = useMemo(() => {
    const userIds = [...new Set(schedules.map(s => s.user_id))];
    
    // Build list of all employees with schedules
    const result = [];
    
    userIds.forEach(userId => {
      // First try to find in employees list
      let emp = employees.find(e => e.user_id === userId);
      
      if (emp) {
        // Get site info from schedules if not on employee
        const userSchedules = schedules.filter(s => s.user_id === userId);
        const siteName = emp.site_id 
          ? (sites.find(s => s.site_id === emp.site_id)?.name || 'Sans site')
          : (userSchedules[0]?.site_name || 'Sans site');
        
        result.push({
          ...emp,
          site_name: siteName
        });
      } else {
        // Employee not in list (deactivated/deleted) - create from schedule data
        const userSchedules = schedules.filter(s => s.user_id === userId);
        const firstSchedule = userSchedules[0];
        
        if (firstSchedule) {
          result.push({
            user_id: userId,
            name: firstSchedule.user_name || 'Inconnu',
            first_name: firstSchedule.first_name,
            last_name: firstSchedule.last_name,
            email: firstSchedule.user_email || '',
            site_name: firstSchedule.site_name || 'Sans site',
            isDeactivated: true // Mark as deactivated for UI indication
          });
        }
      }
    });
    
    return result.sort((a, b) => getEmployeeName(a).localeCompare(getEmployeeName(b)));
  }, [schedules, employees, sites]);

  // Filter employees based on status filter
  const filteredEmployees = useMemo(() => {
    if (statusFilter === 'all') return employeesWithData;
    if (statusFilter === 'active') return employeesWithData.filter(emp => isEmployeeActive(emp));
    if (statusFilter === 'inactive') return employeesWithData.filter(emp => !isEmployeeActive(emp));
    return employeesWithData;
  }, [employeesWithData, statusFilter]);

  // Group employees by site
  const employeesBySite = useMemo(() => {
    const grouped = {};
    
    filteredEmployees.forEach(emp => {
      const siteName = emp.site_name || 'Sans site';
      if (!grouped[siteName]) {
        grouped[siteName] = [];
      }
      grouped[siteName].push(emp);
    });
    
    // Sort employees alphabetically within each site
    Object.keys(grouped).forEach(siteName => {
      grouped[siteName].sort((a, b) => getEmployeeName(a).localeCompare(getEmployeeName(b)));
    });
    
    // Return sorted by site name
    const sortedSites = Object.keys(grouped).sort((a, b) => {
      if (a === 'Sans site') return 1;
      if (b === 'Sans site') return -1;
      return a.localeCompare(b);
    });
    
    return { grouped, sortedSites };
  }, [filteredEmployees]);

  // Toggle site expansion
  const toggleSite = (siteName) => {
    setExpandedSites(prev => ({
      ...prev,
      [siteName]: !prev[siteName]
    }));
  };

  // Calculate individual employee data
  const calculateEmployeeData = (employee) => {
    if (!employee) return null;
    
    const { dates } = getDateRange();
    const employeeSchedules = schedules.filter(s => s.user_id === employee.user_id);
    
    let weekdayHours = 0;
    let sundayHours = 0;
    let holidayHours = 0;
    let totalHours = 0;
    
    const dailyDetails = [];
    
    dates.forEach(date => {
      const daySchedule = employeeSchedules.find(s => s.date === date);
      const hours = daySchedule ? getScheduleHours(daySchedule) : 0;
      const isHoliday = isPublicHoliday(date, publicHolidays);
      const isSun = isSunday(date);
      const holidayName = getHolidayName(date, publicHolidays);
      
      if (hours > 0) {
        if (isHoliday) {
          holidayHours += hours;
        } else if (isSun) {
          sundayHours += hours;
        } else {
          weekdayHours += hours;
        }
        totalHours += hours;
      }
      
      dailyDetails.push({
        date,
        hours,
        schedule: daySchedule,
        isHoliday,
        holidayName,
        isSunday: isSun,
        tourType: daySchedule?.tour_type,
        delivered: daySchedule?.delivered,
        isColisPriv: daySchedule ? isColisPriveSchedule(daySchedule) : false,
        siteName: daySchedule?.site_name,
        status: daySchedule?.status
      });
    });
    
    return { weekdayHours, sundayHours, holidayHours, totalHours, dailyDetails };
  };

  const selectedEmployeeData = useMemo(() => {
    return calculateEmployeeData(selectedEmployee);
  }, [selectedEmployee, schedules, manualHours, publicHolidays, currentDate, viewMode]);

  const handleEditHours = (date) => {
    if (!selectedEmployee) return;
    const key = `${selectedEmployee.user_id}_${date}`;
    setEditMode(prev => ({ ...prev, [key]: true }));
  };

  const persistManualHours = async (date, value) => {
    const key = `${selectedEmployee.user_id}_${date}`;
    const schedule = schedules.find(s => s.user_id === selectedEmployee.user_id && s.date === date);
    if (!schedule?.schedule_id) {
      toast.error('Aucune mission enregistrée ce jour : heures non modifiables.');
      return;
    }
    try {
      if (value === null) {
        await axios.delete(`${API}/schedule/${schedule.schedule_id}/manual-hours`, { withCredentials: true });
      } else {
        await axios.put(`${API}/schedule/${schedule.schedule_id}/manual-hours`, { manual_hours: value }, { withCredentials: true });
      }
      setSchedules(prev => prev.map(s => (s.schedule_id === schedule.schedule_id ? { ...s, manual_hours: value } : s)));
      setEditMode(prev => ({ ...prev, [key]: false }));
      toast.success(value === null ? 'Heures recalculées automatiquement (enregistré)' : 'Heures enregistrées');
    } catch (error) {
      toast.error(`Heures non enregistrées : ${typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'vérifiez la connexion puis réessayez'}`);
    }
  };

  const handleSaveHours = (date, hours) => {
    if (!selectedEmployee) return;
    persistManualHours(date, parseFloat(hours) || 0);
  };

  const handleCancelEdit = (date) => {
    if (!selectedEmployee) return;
    const key = `${selectedEmployee.user_id}_${date}`;
    setEditMode(prev => ({ ...prev, [key]: false }));
  };

  const handleResetToAuto = (date) => {
    if (!selectedEmployee) return;
    persistManualHours(date, null);
  };

  const getPeriodDisplay = () => {
    const { start, end } = getDateRange();
    if (viewMode === 'day') {
      return new Date(start + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } else if (viewMode === 'week') {
      const startDate = new Date(start + 'T12:00:00');
      const endDate = new Date(end + 'T12:00:00');
      return `Semaine du ${startDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} au ${endDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`;
    } else {
      return new Date(currentDate).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    }
  };

  const getDayLabel = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <Layout user={user} activeModule={activeModule}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F172A]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#0F172A] font-['Chivo'] flex items-center gap-3">
              <Calculator className="w-8 h-8" />
              Calcul Salaire
            </h1>
            <p className="text-gray-500 mt-1">
              {displayMode === 'individual' ? 'Calcul individuel des heures par salarié' : 'Vue tableau par site'}
            </p>
          </div>
          
          {/* Display Mode Toggle */}
          {population === 'drivers' && <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setDisplayMode('individual')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                displayMode === 'individual' 
                  ? 'bg-white shadow text-blue-600 font-medium' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <User className="w-4 h-4" />
              Individuel
            </button>
            <button
              onClick={() => setDisplayMode('grid')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                displayMode === 'grid' 
                  ? 'bg-white shadow text-blue-600 font-medium' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Grid3X3 className="w-4 h-4" />
              Tableau par site
            </button>
          </div>}
        </div>

        {canManageInternal(user) && <div className="flex flex-wrap gap-2 border-b pb-4" data-testid="salary-population-tabs"><Button variant={population === 'drivers' ? 'default' : 'outline'} onClick={() => setPopulation('drivers')} data-testid="salary-drivers-tab">Chauffeurs-livreurs</Button><Button variant={population === 'internal' ? 'default' : 'outline'} onClick={() => setPopulation('internal')} data-testid="salary-internal-tab">Dispatchers / DSP</Button></div>}
        {population === 'internal' && canManageInternal(user) && <InternalSalarySection user={user} period={getDateRange()} viewMode={viewMode} setViewMode={setViewMode} navigate={navigate} setCurrentDate={setCurrentDate} />}

        {/* GRID VIEW MODE */}
        {population === 'drivers' && displayMode === 'grid' && (
          <GridViewBysite 
            employees={employees}
            schedules={schedules}
            sites={sites}
            currentDate={currentDate}
            setCurrentDate={setCurrentDate}
            viewMode={viewMode}
            setViewMode={setViewMode}
            getDateRange={getDateRange}
            getScheduleHours={getScheduleHours}
            getEmployeeName={getEmployeeName}
            isColisPriveSchedule={isColisPriveSchedule}
            publicHolidays={publicHolidays}
            navigate={navigate}
            selectedSiteFilter={selectedSiteFilter}
            setSelectedSiteFilter={setSelectedSiteFilter}
            refreshData={fetchData}
          />
        )}

        {/* INDIVIDUAL VIEW MODE (existing) */}
        {population === 'drivers' && displayMode === 'individual' && (
          <>
        {/* Employee Selector with Filters */}
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardContent className="p-4">
            {/* Status Filter */}
            <div className="flex items-center gap-4 mb-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-blue-600" />
                <span className="font-semibold text-gray-700">Filtrer par statut :</span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant={statusFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter('all')}
                >
                  Tous ({employeesWithData.length})
                </Button>
                <Button
                  variant={statusFilter === 'active' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter('active')}
                  className={statusFilter === 'active' ? 'bg-green-600' : ''}
                >
                  Actifs ({employeesWithData.filter(e => isEmployeeActive(e)).length})
                </Button>
                <Button
                  variant={statusFilter === 'inactive' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter('inactive')}
                  className={statusFilter === 'inactive' ? 'bg-orange-600' : ''}
                >
                  Désactivés ({employeesWithData.filter(e => !isEmployeeActive(e)).length})
                </Button>
              </div>
            </div>

            {/* Employee Selector */}
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                <span className="font-semibold text-gray-700">Sélectionner un salarié :</span>
              </div>
              
              <div className="relative flex-1 min-w-[300px]">
                <button
                  onClick={() => setShowEmployeeDropdown(!showEmployeeDropdown)}
                  className="w-full flex items-center gap-2 px-4 py-2 bg-white border-2 border-blue-300 rounded-lg hover:border-blue-500 transition-colors justify-between"
                >
                  <span className={selectedEmployee ? 'text-gray-900 font-medium' : 'text-gray-400'}>
                    {selectedEmployee ? (
                      <>
                        {getEmployeeName(selectedEmployee)}
                        <span className="text-gray-500 text-sm ml-2">({selectedEmployee.site_name})</span>
                      </>
                    ) : 'Choisir un salarié...'}
                  </span>
                  <ChevronDown className={`w-5 h-5 transition-transform ${showEmployeeDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {showEmployeeDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[400px] overflow-y-auto">
                    {filteredEmployees.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        Aucun salarié avec des prestations pour cette période
                      </div>
                    ) : (
                      employeesBySite.sortedSites.map(siteName => (
                        <div key={siteName}>
                          {/* Site Header */}
                          <button
                            onClick={() => toggleSite(siteName)}
                            className="w-full flex items-center justify-between px-4 py-2 bg-gray-100 hover:bg-gray-200 transition-colors sticky top-0"
                          >
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-blue-600" />
                              <span className="font-semibold text-gray-800">{siteName}</span>
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                {employeesBySite.grouped[siteName].length}
                              </span>
                            </div>
                            {expandedSites[siteName] === false ? (
                              <ChevronDown className="w-4 h-4 text-gray-500" />
                            ) : (
                              <ChevronUp className="w-4 h-4 text-gray-500" />
                            )}
                          </button>
                          
                          {/* Employees in this site (expanded by default) */}
                          {expandedSites[siteName] !== false && (
                            <div>
                              {employeesBySite.grouped[siteName].map(emp => (
                                <button
                                  key={emp.user_id}
                                  onClick={() => {
                                    setSelectedEmployee(emp);
                                    setShowEmployeeDropdown(false);
                                  }}
                                  className={`w-full text-left px-6 py-2 hover:bg-blue-50 transition-colors flex items-center justify-between border-l-4 ${
                                    selectedEmployee?.user_id === emp.user_id ? 'bg-blue-100 font-medium border-blue-500' : 'border-transparent'
                                  } ${!isEmployeeActive(emp) ? 'text-gray-500' : ''}`}
                                >
                                  <span>{getEmployeeName(emp)}</span>
                                  <div className="flex items-center gap-2">
                                    {emp.isDeactivated && (
                                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Désactivé</span>
                                    )}
                                    {emp.active === false && !emp.isDeactivated && (
                                      <span className="text-xs bg-orange-200 text-orange-600 px-2 py-0.5 rounded">Inactif</span>
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              
              <span className="text-sm text-gray-500">
                {filteredEmployees.length} salarié(s) • {employeesBySite.sortedSites.length} site(s)
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Period Selector */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex gap-2">
                <Button
                  variant={viewMode === 'day' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('day')}
                >
                  Jour
                </Button>
                <Button
                  variant={viewMode === 'week' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('week')}
                >
                  Semaine
                </Button>
                <Button
                  variant={viewMode === 'month' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('month')}
                >
                  Mois
                </Button>
              </div>
              
              <div className="flex items-center gap-4">
                <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="font-semibold text-[#0F172A] min-w-[300px] text-center">
                  {getPeriodDisplay()}
                </span>
                <Button variant="outline" size="sm" onClick={() => navigate(1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Employee Details */}
        {!selectedEmployee ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 text-lg">Sélectionnez un salarié pour voir le détail de ses heures</p>
              <p className="text-gray-400 text-sm mt-2">
                {employeesWithData.length} salarié(s) avec prestations pour cette période
              </p>
            </CardContent>
          </Card>
        ) : selectedEmployeeData ? (
          <>
            {/* Employee Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-gray-50 border-2 border-gray-200">
                <CardContent className="p-4 text-center">
                  <Clock className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                  <p className="text-2xl font-bold text-gray-800">{formatHoursHm(selectedEmployeeData.weekdayHours)}</p>
                  <p className="text-xs text-gray-500">({formatHoursDecimalFr(selectedEmployeeData.weekdayHours)} heures)</p>
                  <p className="text-sm text-gray-500">Heures semaine</p>
                </CardContent>
              </Card>
              <Card className="bg-orange-50 border-2 border-orange-200">
                <CardContent className="p-4 text-center">
                  <Sun className="w-6 h-6 mx-auto mb-2 text-orange-600" />
                  <p className="text-2xl font-bold text-orange-800">{formatHoursHm(selectedEmployeeData.sundayHours)}</p>
                  <p className="text-xs text-orange-600">({formatHoursDecimalFr(selectedEmployeeData.sundayHours)} heures)</p>
                  <p className="text-sm text-orange-600">Heures dimanche</p>
                </CardContent>
              </Card>
              <Card className="bg-red-50 border-2 border-red-200">
                <CardContent className="p-4 text-center">
                  <Star className="w-6 h-6 mx-auto mb-2 text-red-600" />
                  <p className="text-2xl font-bold text-red-800">{formatHoursHm(selectedEmployeeData.holidayHours)}</p>
                  <p className="text-xs text-red-600">({formatHoursDecimalFr(selectedEmployeeData.holidayHours)} heures)</p>
                  <p className="text-sm text-red-600">Heures fériés</p>
                </CardContent>
              </Card>
              <Card className="bg-blue-100 border-2 border-blue-300">
                <CardContent className="p-4 text-center">
                  <Calculator className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                  <p className="text-2xl font-bold text-blue-800">{formatHoursHm(selectedEmployeeData.totalHours)}</p>
                  <p className="text-xs text-blue-700">({formatHoursDecimalFr(selectedEmployeeData.totalHours)} heures)</p>
                  <p className="text-sm text-blue-600 font-semibold">TOTAL</p>
                </CardContent>
              </Card>
            </div>

            {/* Employee Name Header */}
            <Card className="bg-[#0F172A]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-white flex items-center gap-3">
                      <User className="w-6 h-6" />
                      {getEmployeeName(selectedEmployee)}
                    </h2>
                    <p className="text-gray-300 text-sm mt-1">{selectedEmployee.email}</p>
                  </div>
                  {(selectedEmployee.isDeactivated || selectedEmployee.active === false) && (
                    <span className="px-3 py-1 bg-orange-500 text-white text-sm rounded-full">
                      Salarié désactivé
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Daily Details Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Détail journalier - {getPeriodDisplay()}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3 font-semibold">Date</th>
                        <th className="text-left p-3 font-semibold">Type</th>
                        <th className="text-left p-3 font-semibold">Détail prestation</th>
                        <th className="text-center p-3 font-semibold">Heures</th>
                        <th className="text-center p-3 font-semibold">Catégorie</th>
                        <th className="text-center p-3 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedEmployeeData.dailyDetails.map((detail) => {
                        const key = `${selectedEmployee.user_id}_${detail.date}`;
                        const isManual = detail.schedule ? hasManualHours(detail.schedule) : false;
                        const isEditing = editMode[key];
                        
                        return (
                          <tr 
                            key={detail.date} 
                            className={`border-b hover:bg-gray-50 ${
                              detail.isHoliday ? 'bg-red-50' : 
                              detail.isSunday ? 'bg-orange-50' : ''
                            }`}
                          >
                            <td className="p-3">
                              <div className="font-medium">
                                {new Date(detail.date + 'T12:00:00').toLocaleDateString('fr-FR', { 
                                  weekday: 'long', 
                                  day: 'numeric',
                                  month: 'short'
                                })}
                              </div>
                              {detail.holidayName && (
                                <span className="text-xs text-red-600 font-medium">{detail.holidayName}</span>
                              )}
                            </td>
                            <td className="p-3">
                              {detail.schedule ? (
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  detail.isColisPriv ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                                }`}>
                                  {detail.isColisPriv ? 'Colis Privé' : 'Amazon'}
                                </span>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </td>
                            <td className="p-3">
                              {detail.schedule ? (
                                <div className="text-sm">
                                  {detail.isColisPriv ? (
                                    <span>
                                      Mission {getValidationDisplayLabel(detail.schedule).toLowerCase()}
                                      {detail.delivered !== undefined && detail.delivered !== null ? ` • ${detail.delivered} colis livrés` : ''}
                                    </span>
                                  ) : (
                                    <span>{detail.tourType || detail.siteName || '-'}</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400 text-sm">Pas de prestation</span>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              {isEditing ? (
                                <div className="flex items-center justify-center gap-1">
                                  <Input
                                    type="number"
                                    min="0"
                                    max="24"
                                    step="0.5"
                                    defaultValue={detail.hours}
                                    className="w-20 h-8 text-center"
                                    id={`input-${key}`}
                                    data-testid={`salary-hours-input-${detail.date}`}
                                  />
                                </div>
                              ) : (
                                <span className={`text-sm font-bold ${
                                  detail.hours > 0 ? 'text-gray-900' : 'text-gray-300'
                                } ${isManual ? 'text-blue-600' : ''}`}>
                                  {formatHoursHm(detail.hours)}
                                  {isManual && <Edit2 className="w-3 h-3 inline ml-1 text-blue-500" />}
                                </span>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              {detail.hours > 0 ? (
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  detail.isHoliday ? 'bg-red-200 text-red-700' :
                                  detail.isSunday ? 'bg-orange-200 text-orange-700' :
                                  'bg-gray-200 text-gray-700'
                                }`}>
                                  {detail.isHoliday ? 'Férié' : detail.isSunday ? 'Dimanche' : 'Semaine'}
                                </span>
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              {isEditing ? (
                                <div className="flex items-center justify-center gap-1">
                                  <button
                                    onClick={() => {
                                      const input = document.getElementById(`input-${key}`);
                                      handleSaveHours(detail.date, input.value);
                                    }}
                                    className="p-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded"
                                    title="Enregistrer"
                                    data-testid={`salary-hours-save-${detail.date}`}
                                  >
                                    <Save className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleCancelEdit(detail.date)}
                                    className="p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded"
                                    title="Annuler"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                  {isManual && (
                                    <button
                                      onClick={() => handleResetToAuto(detail.date)}
                                      className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded text-xs"
                                      title="Remettre en calcul automatique"
                                      data-testid={`salary-hours-reset-${detail.date}`}
                                    >
                                      Auto
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleEditHours(detail.date)}
                                  className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                  title="Modifier les heures"
                                  data-testid={`salary-hours-edit-${detail.date}`}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-[#0F172A] text-white font-bold">
                        <td className="p-3" colSpan={3}>TOTAL {getEmployeeName(selectedEmployee)}</td>
                        <td className="p-3 text-center text-base">
                          <div className="font-bold text-xl">{formatHoursHm(selectedEmployeeData.totalHours)}</div>
                          <div className="text-xs text-gray-200">({formatHoursDecimalFr(selectedEmployeeData.totalHours)} heures)</div>
                        </td>
                        <td className="p-3 text-center text-sm">
                          <div>Sem: {formatHoursHm(selectedEmployeeData.weekdayHours)}</div>
                          <div>Dim: {formatHoursHm(selectedEmployeeData.sundayHours)}</div>
                          <div>Fér: {formatHoursHm(selectedEmployeeData.holidayHours)}</div>
                        </td>
                        <td className="p-3"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        ) : null}

        {/* Calculation Rules Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Règles de calcul</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-gray-600">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="font-semibold text-gray-800 mb-2">Amazon :</p>
                <ul className="space-y-1">
                  <li>• NEXT : 7h</li>
                  <li>• SDA (SAME A) : 4h</li>
                  <li>• SDB (SAME B) : 3h</li>
                  <li>• SDC (SAME C) : 3h</li>
                  <li>• SDA+B, SDA+C, SDB+C : 7h</li>
                  <li>• SDA+B+C (SAME A+B+C) : 10h</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-gray-800 mb-2">Colis Privé :</p>
                <ul className="space-y-1">
                  <li>• 1 mission validée = 7h45</li>
                  <li>• Soit 7,75 heures en format décimal</li>
                  <li>• Mission non validée = 0 heure</li>
                </ul>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t">
              <p className="font-semibold text-gray-800 mb-2">Jours fériés pris en compte :</p>
              <p className="text-xs">1er janvier • Lundi de Pâques • 1er mai • 8 mai • 14 juillet • 15 août • 1er novembre • 11 novembre • 25 décembre</p>
            </div>
          </CardContent>
        </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

// =====================================================
// GRID VIEW COMPONENT - Display by Site (visual only)
// =====================================================
const GridViewBysite = ({
  employees,
  schedules,
  sites,
  currentDate,
  setCurrentDate,
  viewMode,
  setViewMode,
  getDateRange,
  getScheduleHours,
  getEmployeeName,
  isColisPriveSchedule,
  publicHolidays,
  navigate,
  selectedSiteFilter,
  setSelectedSiteFilter,
  refreshData
}) => {
  const { dates } = getDateRange();
  
  // State for manual editing
  const [editingCell, setEditingCell] = useState(null); // { oddate }
  const [editValue, setEditValue] = useState('');
  const [savingCell, setSavingCell] = useState(null);
  const [gridEmployeeSearch, setGridEmployeeSearch] = useState('');
  const [selectedGridEmployeeId, setSelectedGridEmployeeId] = useState('all');
  const isMobileViewport = typeof window !== 'undefined' ? window.innerWidth < 640 : false;

  const getStatusReasonLabel = (status) => {
    const integrationLabel = isMobileViewport ? 'Intégration' : 'En cours d’intégration';
    const map = {
      repos: 'Repos',
      repos_force: 'Repos forcé',
      absent: 'Absent',
      am: 'AM',
      cp: 'CP',
      css: 'CSS',
      fdc: 'FDC',
      fpe: 'FPE',
      dem: 'Démission',
      adp: 'Abandon de poste',
      map: 'MAP',
      disponible: 'Disponible',
      repos_demande: 'Repos',
      formation_binome: 'Formation Binôme',
      formation_amzl: 'Formation AMZL',
      formation_safety: 'Formation Safety',
      en_cours_integration: integrationLabel,
      licenciement: 'Licenciement',
      rupture_conventionnelle: 'Rupture conventionnelle',
      prevu: 'Prévu',
      present: 'Présent'
    };
    return map[status] || null;
  };

  const getNoHoursReason = (schedule) => {
    if (!schedule) return null;

    if (hasPendingMissionValidation(schedule)) return isMobileViewport ? 'À valider' : 'En attente de validation';
    if (hasRejectedMissionValidation(schedule)) return 'Refusée';
    if (isPresentWithoutValidation(schedule)) return 'Validation manquante';

    const rawStatus = String(schedule.status || '').trim().toLowerCase();
    if (!rawStatus) return null;
    const mapped = getStatusReasonLabel(rawStatus);
    if (mapped) return mapped;
    return rawStatus.replace(/_/g, ' ');
  };
  
  // Get unique sites from schedules (include all sites with prestations)
  const sitesFromSchedules = useMemo(() => {
    const siteSet = new Set();
    schedules.forEach(s => {
      if (s.site_name) siteSet.add(s.site_name);
    });
    // Also add sites from site list
    sites.forEach(s => siteSet.add(s.name));
    return Array.from(siteSet).sort();
  }, [schedules, sites]);

  // Get employees who have at least one prestation in the period
  const employeesWithPrestations = useMemo(() => {
    const empIds = new Set();
    schedules.forEach(s => {
      if ((s.status === 'present' || s.status === 'prevu') && s.user_id) {
        empIds.add(s.user_id);
      }
    });
    return employees.filter(e => empIds.has(e.user_id));
  }, [employees, schedules]);

  // Filter employees by selected site
  const filteredEmployeesForGrid = useMemo(() => {
    if (selectedSiteFilter === 'all') {
      return employeesWithPrestations;
    }
    
    // Find employees who have worked on this site during the period
    const empIdsForSite = new Set();
    schedules.forEach(s => {
      if ((s.status === 'present' || s.status === 'prevu') && 
          s.site_name === selectedSiteFilter) {
        empIdsForSite.add(s.user_id);
      }
    });
    
    return employeesWithPrestations.filter(e => empIdsForSite.has(e.user_id));
  }, [employeesWithPrestations, schedules, selectedSiteFilter]);

  const sortedFilteredEmployees = useMemo(() => {
    return [...filteredEmployeesForGrid].sort((a, b) => getEmployeeName(a).localeCompare(getEmployeeName(b)));
  }, [filteredEmployeesForGrid, getEmployeeName]);

  const employeeFilterOptions = useMemo(() => {
    const term = gridEmployeeSearch.trim().toLowerCase();
    if (!term) return sortedFilteredEmployees;

    return sortedFilteredEmployees.filter(emp => {
      const fullName = getEmployeeName(emp).toLowerCase();
      const firstName = String(emp.first_name || '').toLowerCase();
      const lastName = String(emp.last_name || '').toLowerCase();
      return fullName.includes(term) || firstName.includes(term) || lastName.includes(term);
    });
  }, [sortedFilteredEmployees, gridEmployeeSearch, getEmployeeName]);

  const displayedEmployeesForGrid = useMemo(() => {
    if (selectedGridEmployeeId === 'all') return filteredEmployeesForGrid;
    return filteredEmployeesForGrid.filter(emp => emp.user_id === selectedGridEmployeeId);
  }, [filteredEmployeesForGrid, selectedGridEmployeeId]);

  useEffect(() => {
    if (selectedGridEmployeeId === 'all') return;
    const stillVisible = filteredEmployeesForGrid.some(emp => emp.user_id === selectedGridEmployeeId);
    if (!stillVisible) {
      setSelectedGridEmployeeId('all');
    }
  }, [filteredEmployeesForGrid, selectedGridEmployeeId]);

  const selectedEmployeeIndex = useMemo(() => {
    if (selectedGridEmployeeId === 'all') return -1;
    return sortedFilteredEmployees.findIndex(emp => emp.user_id === selectedGridEmployeeId);
  }, [selectedGridEmployeeId, sortedFilteredEmployees]);

  const goToPreviousEmployee = () => {
    if (selectedEmployeeIndex <= 0) return;
    const previous = sortedFilteredEmployees[selectedEmployeeIndex - 1];
    if (previous) setSelectedGridEmployeeId(previous.user_id);
  };

  const goToNextEmployee = () => {
    if (selectedEmployeeIndex === -1 || selectedEmployeeIndex >= sortedFilteredEmployees.length - 1) return;
    const next = sortedFilteredEmployees[selectedEmployeeIndex + 1];
    if (next) setSelectedGridEmployeeId(next.user_id);
  };

  const getCellDisplayValue = (employeeId, date) => {
    const schedule = getScheduleForDate(employeeId, date);
    const hours = getHoursForDate(employeeId, date);

    if (hours !== null) {
      return {
        type: 'hours',
        value: formatHoursHm(hours),
        hours
      };
    }

    if (!schedule) {
      return { type: 'empty', value: '' };
    }

    const reason = getNoHoursReason(schedule);
    if (reason) {
      return { type: 'reason', value: reason };
    }

    return { type: 'plus', value: '+' };
  };

  // Get hours for a specific employee on a specific date
  const getHoursForDate = (employeeId, date) => {
    const schedule = schedules.find(s => s.user_id === employeeId && s.date === date);
    if (!schedule) return null;
    
    // If filtering by site, only count hours for that site
    if (selectedSiteFilter !== 'all' && schedule.site_name !== selectedSiteFilter) {
      return null;
    }
    
    const hours = getScheduleHours(schedule);
    return hours > 0 ? hours : null;
  };

  // Get schedule for a specific employee on a specific date
  const getScheduleForDate = (employeeId, date) => {
    return schedules.find(s => s.user_id === employeeId && s.date === date);
  };

  // Handle cell click to start editing
  const handleCellClick = (employeeId, date) => {
    const schedule = getScheduleForDate(employeeId, date);
    if (!schedule) return; // Can only edit existing schedules
    
    const currentHours = getScheduleHours(schedule);
    setEditingCell({ employeeId, date, scheduleId: schedule.schedule_id });
    setEditValue(currentHours > 0 ? currentHours.toString() : '');
  };

  // Handle save of edited hours
  const handleSaveHours = async (employeeId, date) => {
    if (!editingCell) return;
    
    const schedule = getScheduleForDate(employeeId, date);
    if (!schedule) return;
    
    const newHours = parseFloat(editValue) || 0;
    
    setSavingCell(`${employeeId}-${date}`);
    try {
      await axios.put(`${API}/schedule/${schedule.schedule_id}/manual-hours`, {
        manual_hours: newHours
      }, { withCredentials: true });
      
      // Refresh data without page reload - stay on grid view
      setEditingCell(null);
      setEditValue('');
      await refreshData();
    } catch (error) {
      console.error('Failed to save hours:', error);
      toast.error(`Heures non enregistrées : ${typeof error?.response?.data?.detail === 'string' ? error.response.data.detail : 'vérifiez la connexion puis réessayez'}`);
    } finally {
      setSavingCell(null);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // Calculate total hours for an employee
  const getTotalHours = (employeeId) => {
    return dates.reduce((total, date) => {
      const hours = getHoursForDate(employeeId, date);
      return total + (hours || 0);
    }, 0);
  };

  // Calculate Sunday hours for an employee
  const getSundayHours = (employeeId) => {
    return dates.reduce((total, date) => {
      if (isSunday(date)) {
        const hours = getHoursForDate(employeeId, date);
        return total + (hours || 0);
      }
      return total;
    }, 0);
  };

  // Calculate holiday hours for an employee (excluding Sundays to avoid double count)
  const getHolidayHours = (employeeId) => {
    return dates.reduce((total, date) => {
      if (isHoliday(date) && !isSunday(date)) {
        const hours = getHoursForDate(employeeId, date);
        return total + (hours || 0);
      }
      return total;
    }, 0);
  };

  // Calculate weekday hours (not Sunday, not holiday)
  const getWeekdayHours = (employeeId) => {
    return dates.reduce((total, date) => {
      if (!isSunday(date) && !isHoliday(date)) {
        const hours = getHoursForDate(employeeId, date);
        return total + (hours || 0);
      }
      return total;
    }, 0);
  };

  // Separate active and inactive employees
  const activeEmployees = useMemo(() => {
    return displayedEmployeesForGrid
      .filter(e => e.active !== false)
      .sort((a, b) => getEmployeeName(a).localeCompare(getEmployeeName(b)));
  }, [displayedEmployeesForGrid, getEmployeeName]);

  const inactiveEmployees = useMemo(() => {
    return displayedEmployeesForGrid
      .filter(e => e.active === false)
      .sort((a, b) => getEmployeeName(a).localeCompare(getEmployeeName(b)));
  }, [displayedEmployeesForGrid, getEmployeeName]);

  // Check if date is Sunday
  const isSunday = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.getDay() === 0;
  };

  // Check if date is holiday
  const isHoliday = (dateStr) => {
    return publicHolidays.some(h => h.date === dateStr);
  };

  // Format date for column header
  const formatColumnDate = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00');
    const day = date.getDate();
    const weekday = date.toLocaleDateString('fr-FR', { weekday: 'short' }).substring(0, 2);
    return { day, weekday };
  };

  const formatMonthYear = () => {
    return currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  // Export to Excel function
  const exportToExcel = () => {
    // Prepare data for Excel
    const exportData = [];
    const allEmployees = [...activeEmployees, ...inactiveEmployees];
    
    // Helper to format date for Excel
    const formatDateForExcel = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr + 'T12:00:00');
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };
    
    // Create header row with dates - NEW: Added trial start date column for all, deactivation date & reason for inactive
    const headerRow = ['Chauffeur', 'Statut', 'Début période essai'];
    dates.forEach(date => {
      const d = new Date(date + 'T12:00:00');
      const dayName = d.toLocaleDateString('fr-FR', { weekday: 'short' });
      const dayNum = d.getDate();
      headerRow.push(`${dayName} ${dayNum}`);
    });
    headerRow.push('H. Semaine', 'H. Dimanche', 'H. Fériés', 'TOTAL');
    exportData.push(headerRow);
    
    // Add active employees section
    if (activeEmployees.length > 0) {
      exportData.push(['--- CHAUFFEURS ACTIFS ---', '', '', ...Array(dates.length + 4).fill('')]);
      activeEmployees.forEach(emp => {
        // Get trial start date from employee or trial period
        const trialStartDate = emp.trial_start_date || emp.start_date || '';
        
        const row = [
          getEmployeeName(emp),
          'Actif',
          formatDateForExcel(trialStartDate)
        ];
        
        dates.forEach(date => {
          const cell = getCellDisplayValue(emp.user_id, date);
          row.push(cell?.value ?? '');
        });
        
        const weekdayHours = getWeekdayHours(emp.user_id);
        const sundayHours = getSundayHours(emp.user_id);
        const holidayHours = getHolidayHours(emp.user_id);
        const totalHours = weekdayHours + sundayHours + holidayHours;
        
        row.push(weekdayHours || '', sundayHours || '', holidayHours || '', totalHours || '');
        exportData.push(row);
      });
    }
    
    // Add inactive employees section - WITH deactivation date and termination reason
    if (inactiveEmployees.length > 0) {
      // Special header for inactive section with additional columns
      const inactiveHeader = ['--- ANCIENS CHAUFFEURS / DÉSACTIVÉS ---', '', '', ...Array(dates.length).fill(''), '', '', '', '', 'Date désactivation', 'Cause de fin'];
      exportData.push(inactiveHeader);
      
      inactiveEmployees.forEach(emp => {
        // Get trial start date
        const trialStartDate = emp.trial_start_date || emp.start_date || '';
        
        const row = [
          getEmployeeName(emp),
          'Off',
          formatDateForExcel(trialStartDate)
        ];
        
        dates.forEach(date => {
          const cell = getCellDisplayValue(emp.user_id, date);
          row.push(cell?.value ?? '');
        });
        
        const weekdayHours = getWeekdayHours(emp.user_id);
        const sundayHours = getSundayHours(emp.user_id);
        const holidayHours = getHolidayHours(emp.user_id);
        const totalHours = weekdayHours + sundayHours + holidayHours;
        
        // Add hours + deactivation info for inactive employees
        row.push(
          weekdayHours || '', 
          sundayHours || '', 
          holidayHours || '', 
          totalHours || '',
          formatDateForExcel(emp.deactivation_date) || '',
          emp.termination_reason || ''
        );
        exportData.push(row);
      });
    }
    
    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_array ? XLSX.utils.aoa_to_sheet(exportData) : XLSX.utils.aoa_to_sheet(exportData);
    
    // Set column widths
    const colWidths = [
      { wch: 25 }, // Chauffeur name
      { wch: 10 }, // Statut
      { wch: 14 }, // Début période essai
      ...dates.map(() => ({ wch: 8 })), // Date columns
      { wch: 12 }, // H. Semaine
      { wch: 12 }, // H. Dimanche
      { wch: 12 }, // H. Fériés
      { wch: 10 }, // TOTAL
      { wch: 16 }, // Date désactivation
      { wch: 30 }, // Cause de fin
    ];
    ws['!cols'] = colWidths;
    
    // Generate filename
    const siteName = selectedSiteFilter === 'all' ? 'Tous_Sites' : selectedSiteFilter.replace(/\s+/g, '_');
    const period = formatMonthYear().replace(/\s+/g, '_');
    const filename = `Heures_Travail_${siteName}_${period}.xlsx`;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Heures');
    
    // Download file
    XLSX.writeFile(wb, filename);
  };

  return (
    <div className="space-y-4">
      {/* Controls */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Site Filter */}
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-gray-700">Site :</span>
              <select
                value={selectedSiteFilter}
                onChange={(e) => setSelectedSiteFilter(e.target.value)}
                className="px-3 py-2 border rounded-lg bg-white min-w-[200px]"
              >
                <option value="all">Tous les sites</option>
                {sitesFromSchedules.map(site => (
                  <option key={site} value={site}>{site}</option>
                ))}
              </select>
            </div>

            {/* Employee Filter */}
            <div className="flex items-center gap-2 flex-wrap">
              <User className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-gray-700">Salarié :</span>
              <Input
                value={gridEmployeeSearch}
                onChange={(e) => setGridEmployeeSearch(e.target.value)}
                placeholder="Rechercher nom / prénom"
                className="h-10 w-[200px]"
                data-testid="salary-grid-employee-search-input"
              />
              <select
                value={selectedGridEmployeeId}
                onChange={(e) => setSelectedGridEmployeeId(e.target.value)}
                className="px-3 py-2 border rounded-lg bg-white min-w-[220px]"
                data-testid="salary-grid-employee-filter-select"
              >
                <option value="all">Tous les salariés</option>
                {employeeFilterOptions.map(emp => (
                  <option key={emp.user_id} value={emp.user_id}>
                    {getEmployeeName(emp)}
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToPreviousEmployee}
                  disabled={selectedGridEmployeeId === 'all' || selectedEmployeeIndex <= 0}
                  data-testid="salary-grid-previous-employee-button"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Précédent
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToNextEmployee}
                  disabled={
                    selectedGridEmployeeId === 'all' ||
                    selectedEmployeeIndex === -1 ||
                    selectedEmployeeIndex >= sortedFilteredEmployees.length - 1
                  }
                  data-testid="salary-grid-next-employee-button"
                >
                  Suivant
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>

            {/* Period Selector */}
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-gray-700">Période :</span>
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('week')}
                  className={`px-3 py-1 rounded text-sm ${viewMode === 'week' ? 'bg-white shadow font-medium' : ''}`}
                >
                  Semaine
                </button>
                <button
                  onClick={() => setViewMode('month')}
                  className={`px-3 py-1 rounded text-sm ${viewMode === 'month' ? 'bg-white shadow font-medium' : ''}`}
                >
                  Mois
                </button>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center gap-2 ml-auto">
              <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="font-semibold text-gray-800 min-w-[150px] text-center capitalize">
                {formatMonthYear()}
              </span>
              <Button variant="outline" size="sm" onClick={() => navigate(1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
              
              {/* Export Excel Button */}
              <Button 
                onClick={exportToExcel}
                className="ml-4 bg-green-600 hover:bg-green-700"
                disabled={displayedEmployeesForGrid.length === 0}
              >
                <Download className="w-4 h-4 mr-2" />
                Export Excel
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Grid Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Grid3X3 className="w-5 h-5" />
            {selectedSiteFilter === 'all' ? 'Tous les sites' : selectedSiteFilter}
            <span className="text-sm font-normal text-gray-500">
              ({displayedEmployeesForGrid.length} chauffeur{displayedEmployeesForGrid.length > 1 ? 's' : ''})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100 border-b">
                  <th className="sticky left-0 bg-gray-100 p-2 text-left font-semibold min-w-[180px] z-10">
                    Chauffeur
                  </th>
                  {dates.map(date => {
                    const { day, weekday } = formatColumnDate(date);
                    const sunday = isSunday(date);
                    const holiday = isHoliday(date);
                    return (
                      <th 
                        key={date} 
                        className={`p-1 text-center min-w-[40px] ${
                          sunday ? 'bg-blue-100' : 
                          holiday ? 'bg-amber-100' : ''
                        }`}
                        title={holiday ? publicHolidays.find(h => h.date === date)?.name : ''}
                      >
                        <div className="text-xs text-gray-500">{weekday}</div>
                        <div className={`font-semibold ${sunday ? 'text-blue-600' : holiday ? 'text-amber-600' : ''}`}>
                          {day}
                        </div>
                      </th>
                    );
                  })}
                  <th className="p-2 text-center font-semibold bg-gray-200 min-w-[60px] border-l-2 border-gray-300">
                    H. Sem.
                  </th>
                  <th className="p-2 text-center font-semibold bg-blue-200 min-w-[60px]" title="Heures Dimanches">
                    H. Dim.
                  </th>
                  <th className="p-2 text-center font-semibold bg-amber-200 min-w-[60px]" title="Heures Jours Fériés">
                    H. Fériés
                  </th>
                  <th className="p-2 text-center font-semibold bg-gray-300 min-w-[70px] border-l-2 border-gray-400">
                    TOTAL
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayedEmployeesForGrid.length === 0 ? (
                  <tr>
                    <td colSpan={dates.length + 5} className="p-8 text-center text-gray-500">
                      Aucun chauffeur trouvé pour ce filtre
                    </td>
                  </tr>
                ) : (
                  <>
                    {/* ACTIVE EMPLOYEES SECTION */}
                    {activeEmployees.length > 0 && (
                      <>
                        <tr className="bg-green-600">
                          <td colSpan={dates.length + 5} className="p-2 text-white font-bold text-sm">
                            ● CHAUFFEURS ACTIFS ({activeEmployees.length})
                          </td>
                        </tr>
                        {activeEmployees.map(emp => {
                          const weekdayHours = getWeekdayHours(emp.user_id);
                          const sundayHours = getSundayHours(emp.user_id);
                          const holidayHours = getHolidayHours(emp.user_id);
                          const totalHours = weekdayHours + sundayHours + holidayHours;
                          
                          return (
                            <tr 
                              key={emp.user_id} 
                              className="border-b hover:bg-gray-50"
                            >
                              <td className="sticky left-0 bg-white p-2 font-medium border-r z-10">
                                {getEmployeeName(emp)}
                              </td>
                              {dates.map(date => {
                                const cellDisplay = getCellDisplayValue(emp.user_id, date);
                                const hours = cellDisplay.type === 'hours' ? cellDisplay.hours : null;
                                const sunday = isSunday(date);
                                const holiday = isHoliday(date);
                                const schedule = getScheduleForDate(emp.user_id, date);
                                const isEditing = editingCell?.employeeId === emp.user_id && editingCell?.date === date;
                                const isSaving = savingCell === `${emp.user_id}-${date}`;
                                const hasManual = schedule && (schedule.manual_hours !== undefined && schedule.manual_hours !== null);
                                
                                return (
                                  <td 
                                    key={date} 
                                    className={`p-1 text-center cursor-pointer hover:bg-gray-100 ${
                                      sunday ? 'bg-blue-50' : 
                                      holiday ? 'bg-amber-50' : ''
                                    }`}
                                    onClick={() => !isEditing && schedule && handleCellClick(emp.user_id, date)}
                                    title={schedule ? "Cliquer pour modifier les heures" : "Pas de planning"}
                                  >
                                    {isEditing ? (
                                      <div className="flex items-center gap-1 justify-center">
                                        <input
                                          type="number"
                                          step="0.5"
                                          min="0"
                                          max="24"
                                          value={editValue}
                                          onChange={(e) => setEditValue(e.target.value)}
                                          className="w-12 px-1 py-0.5 text-xs border rounded text-center"
                                          autoFocus
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleSaveHours(emp.user_id, date);
                                            if (e.key === 'Escape') handleCancelEdit();
                                          }}
                                        />
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleSaveHours(emp.user_id, date); }}
                                          className="p-0.5 text-green-600 hover:bg-green-100 rounded"
                                          disabled={isSaving}
                                        >
                                          <Save className="w-3 h-3" />
                                        </button>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleCancelEdit(); }}
                                          className="p-0.5 text-red-600 hover:bg-red-100 rounded"
                                        >
                                          <X className="w-3 h-3" />
                                        </button>
                                      </div>
                                    ) : cellDisplay.type === 'hours' ? (
                                      <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${
                                        hasManual ? 'ring-2 ring-purple-400 ' : ''
                                      }${
                                        sunday ? 'bg-blue-200 text-blue-800' :
                                        holiday ? 'bg-amber-200 text-amber-800' :
                                        'bg-green-100 text-green-700'
                                      }`} title={hasManual ? 'Heures modifiées manuellement' : ''}>
                                        {formatHoursHm(hours)}
                                      </span>
                                    ) : cellDisplay.type === 'reason' ? (
                                      <span
                                        className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700 border border-slate-200"
                                        data-testid={`salary-grid-reason-badge-${emp.user_id}-${date}`}
                                      >
                                        {cellDisplay.value}
                                      </span>
                                    ) : cellDisplay.type === 'plus' ? (
                                      <span className="text-gray-300 text-xs">+</span>
                                    ) : null}
                                  </td>
                                );
                              })}
                              <td className="p-2 text-center font-semibold bg-gray-100 border-l-2 border-gray-300">
                                {weekdayHours > 0 ? formatHoursHm(weekdayHours) : '-'}
                              </td>
                              <td className="p-2 text-center font-semibold bg-blue-50 text-blue-700">
                                {sundayHours > 0 ? formatHoursHm(sundayHours) : '-'}
                              </td>
                              <td className="p-2 text-center font-semibold bg-amber-50 text-amber-700">
                                {holidayHours > 0 ? formatHoursHm(holidayHours) : '-'}
                              </td>
                              <td className="p-2 text-center font-bold bg-gray-200 border-l-2 border-gray-400">
                                {totalHours > 0 ? formatHoursDual(totalHours) : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </>
                    )}

                    {/* INACTIVE EMPLOYEES SECTION */}
                    {inactiveEmployees.length > 0 && (
                      <>
                        <tr className="bg-gray-500">
                          <td colSpan={dates.length + 5} className="p-2 text-white font-bold text-sm">
                            ○ ANCIENS CHAUFFEURS / DÉSACTIVÉS ({inactiveEmployees.length})
                          </td>
                        </tr>
                        {inactiveEmployees.map(emp => {
                          const weekdayHours = getWeekdayHours(emp.user_id);
                          const sundayHours = getSundayHours(emp.user_id);
                          const holidayHours = getHolidayHours(emp.user_id);
                          const totalHours = weekdayHours + sundayHours + holidayHours;
                          
                          return (
                            <tr 
                              key={emp.user_id} 
                              className="border-b hover:bg-gray-100 bg-gray-50"
                            >
                              <td className="sticky left-0 bg-gray-50 p-2 font-medium border-r z-10">
                                <div className="flex items-center gap-2 text-gray-600">
                                  <span>{getEmployeeName(emp)}</span>
                                  <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-600 rounded">Off</span>
                                </div>
                              </td>
                              {dates.map(date => {
                                const cellDisplay = getCellDisplayValue(emp.user_id, date);
                                const hours = cellDisplay.type === 'hours' ? cellDisplay.hours : null;
                                const sunday = isSunday(date);
                                const holiday = isHoliday(date);
                                const schedule = getScheduleForDate(emp.user_id, date);
                                const isEditing = editingCell?.employeeId === emp.user_id && editingCell?.date === date;
                                const isSaving = savingCell === `${emp.user_id}-${date}`;
                                const hasManual = schedule && (schedule.manual_hours !== undefined && schedule.manual_hours !== null);
                                
                                return (
                                  <td 
                                    key={date} 
                                    className={`p-1 text-center cursor-pointer hover:bg-gray-200 ${
                                      sunday ? 'bg-blue-50/50' : 
                                      holiday ? 'bg-amber-50/50' : ''
                                    }`}
                                    onClick={() => !isEditing && schedule && handleCellClick(emp.user_id, date)}
                                    title={schedule ? "Cliquer pour modifier les heures" : "Pas de planning"}
                                  >
                                    {isEditing ? (
                                      <div className="flex items-center gap-1 justify-center">
                                        <input
                                          type="number"
                                          step="0.5"
                                          min="0"
                                          max="24"
                                          value={editValue}
                                          onChange={(e) => setEditValue(e.target.value)}
                                          className="w-12 px-1 py-0.5 text-xs border rounded text-center"
                                          autoFocus
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleSaveHours(emp.user_id, date);
                                            if (e.key === 'Escape') handleCancelEdit();
                                          }}
                                        />
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleSaveHours(emp.user_id, date); }}
                                          className="p-0.5 text-green-600 hover:bg-green-100 rounded"
                                          disabled={isSaving}
                                        >
                                          <Save className="w-3 h-3" />
                                        </button>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleCancelEdit(); }}
                                          className="p-0.5 text-red-600 hover:bg-red-100 rounded"
                                        >
                                          <X className="w-3 h-3" />
                                        </button>
                                      </div>
                                    ) : cellDisplay.type === 'hours' ? (
                                      <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium opacity-75 ${
                                        hasManual ? 'ring-2 ring-purple-400 ' : ''
                                      }${
                                        sunday ? 'bg-blue-200 text-blue-800' :
                                        holiday ? 'bg-amber-200 text-amber-800' :
                                        'bg-green-100 text-green-700'
                                      }`} title={hasManual ? 'Heures modifiées manuellement' : ''}>
                                        {formatHoursHm(hours)}
                                      </span>
                                    ) : cellDisplay.type === 'reason' ? (
                                      <span
                                        className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700 border border-slate-200"
                                        data-testid={`salary-grid-reason-badge-inactive-${emp.user_id}-${date}`}
                                      >
                                        {cellDisplay.value}
                                      </span>
                                    ) : cellDisplay.type === 'plus' ? (
                                      <span className="text-gray-300 text-xs">+</span>
                                    ) : null}
                                  </td>
                                );
                              })}
                              <td className="p-2 text-center font-semibold bg-gray-100 border-l-2 border-gray-300 text-gray-600">
                                {weekdayHours > 0 ? formatHoursHm(weekdayHours) : '-'}
                              </td>
                              <td className="p-2 text-center font-semibold bg-blue-50/50 text-blue-600">
                                {sundayHours > 0 ? formatHoursHm(sundayHours) : '-'}
                              </td>
                              <td className="p-2 text-center font-semibold bg-amber-50/50 text-amber-600">
                                {holidayHours > 0 ? formatHoursHm(holidayHours) : '-'}
                              </td>
                              <td className="p-2 text-center font-bold bg-gray-200 border-l-2 border-gray-400 text-gray-600">
                                {totalHours > 0 ? formatHoursDual(totalHours) : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex items-center gap-6 text-sm text-gray-600 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="w-4 h-4 bg-green-100 rounded"></span>
          <span>Jour semaine</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-4 h-4 bg-blue-200 rounded"></span>
          <span>Dimanche</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-4 h-4 bg-amber-200 rounded"></span>
          <span>Jour férié</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-4 h-4 bg-green-100 rounded ring-2 ring-purple-400"></span>
          <span>Modifié manuellement</span>
        </div>
        <div className="border-l pl-4 ml-2 flex items-center gap-4">
          <span className="text-xs font-medium">Colonnes résumé :</span>
          <span className="text-xs">H. Sem. = Heures Semaine</span>
          <span className="text-xs">H. Dim. = Heures Dimanches</span>
          <span className="text-xs">H. Fériés = Heures Jours Fériés</span>
        </div>
      </div>
      
      {/* Help text */}
      <p className="text-xs text-gray-500 italic">
        💡 Astuce : Cliquez sur une cellule pour modifier manuellement les heures. Les modifications manuelles sont indiquées par un contour violet.
      </p>
    </div>
  );
};

export default AdminCalculSalaire;
